import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslations } from '@/hooks/useTranslations';
import { DEFAULT_OG_IMAGE } from '@/lib/ogImages';

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  type?: 'website' | 'article' | 'profile';
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    category?: string;
  };
  noindex?: boolean;
  structuredData?: object;
  breadcrumbs?: BreadcrumbItem[];
}

const BASE_URL = 'https://www.klanert.com';
const SITE_NAME = 'Klanert';
const TWITTER_HANDLE = '@klanert_coach';

// Strip HTML, collapse whitespace, truncate at word boundary for meta description
export const buildMetaDescription = (
  source: string | null | undefined,
  fallback: string,
  maxLen = 160,
): string => {
  const raw = (source || '').toString();
  const text = raw
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return fallback;
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut) + '…';
};

export const countWords = (html: string | null | undefined): number => {
  if (!html) return 0;
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text ? text.split(' ').length : 0;
};

const SEO = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  article,
  noindex = false,
  structuredData,
  breadcrumbs,
}: SEOProps) => {
  const location = useLocation();
  const { language } = useTranslations();
  
  const isEnglish = language === 'en';
  const currentUrl = `${BASE_URL}${location.pathname}`;
  
  // Default titles and descriptions based on language
  const defaultTitle = isEnglish 
    ? 'Klanert | High-Performance Coaching & Biohacking'
    : 'Klanert | High-Performance Coaching & Biohacking';
  
  const defaultDescription = isEnglish
    ? 'Science-based biohacking system for entrepreneurs and top performers. More energy, focus, and resilience with measurable results.'
    : 'Wissenschaftlich fundiertes Biohacking-System für Unternehmer und Top-Performer. Mehr Energie, Fokus und Belastbarkeit mit messbaren Ergebnissen.';

  const fullTitle = title ? `${title} | ${SITE_NAME}` : defaultTitle;
  const metaDescription = description || defaultDescription;

  // Base structured data for the website
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    description: defaultDescription,
    inLanguage: isEnglish ? 'en' : 'de',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${BASE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  // Person schema for Jürgen Klanert
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Jürgen Klanert',
    honorificSuffix: 'PhD',
    jobTitle: 'High-Performance Coach',
    url: `${BASE_URL}/ansatz`,
    sameAs: [
      'https://www.linkedin.com/in/juergenklanert/',
      'https://www.instagram.com/juergenklanert',
      'https://www.facebook.com/klanertjuergen/',
      'https://x.com/KlanertJuergen',
    ],
    worksFor: {
      '@type': 'Organization',
      name: 'Human Performance Systems OÜ',
    },
  };

  // Professional service schema
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Klanert High-Performance Coaching',
    description: metaDescription,
    url: BASE_URL,
    provider: personSchema,
    serviceType: 'Business Coaching',
    areaServed: ['DE', 'AT', 'CH'],
  };

  // LocalBusiness schema for local SEO
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/#localbusiness`,
    name: 'Klanert High-Performance Coaching',
    description: isEnglish 
      ? 'Science-based biohacking and high-performance coaching for entrepreneurs and executives.'
      : 'Wissenschaftlich fundiertes Biohacking und High-Performance Coaching für Unternehmer und Führungskräfte.',
    url: BASE_URL,
    telephone: '+49 163 7279 372',
    email: 'juergen@klanert.com',
    image: DEFAULT_OG_IMAGE,
    logo: `${BASE_URL}/favicon.svg`,
    priceRange: '€€€',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Tornimäe tn 5',
      addressLocality: 'Tallinn',
      postalCode: '10145',
      addressCountry: 'EE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 48.1351,
      longitude: 11.5450,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    sameAs: [
      'https://www.linkedin.com/in/juergenklanert/',
      'https://www.instagram.com/juergenklanert',
      'https://www.facebook.com/klanertjuergen/',
      'https://x.com/KlanertJuergen',
    ],
    founder: {
      '@type': 'Person',
      name: 'Jürgen Klanert',
      honorificSuffix: 'PhD',
    },
  };

  // Combine schemas
  const schemas: object[] = [websiteSchema, serviceSchema, localBusinessSchema];
  if (structuredData) {
    schemas.push(structuredData);
  }
  
  // Add breadcrumb schema if provided
  if (breadcrumbs && breadcrumbs.length > 0) {
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${BASE_URL}${item.path}`,
      })),
    };
    schemas.push(breadcrumbSchema);
  }

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={isEnglish ? 'en' : 'de'} />
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <meta name="author" content="Jürgen Klanert" />
      <link rel="canonical" href={currentUrl} />
      
      {/* Language alternates */}
      <link rel="alternate" hrefLang="de" href={`${BASE_URL}${location.pathname.replace(/^\/en/, '')}`} />
      <link rel="alternate" hrefLang="en" href={`${BASE_URL}/en${location.pathname.replace(/^\/en/, '')}`} />
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${location.pathname.replace(/^\/en/, '')}`} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={isEnglish ? 'en_US' : 'de_DE'} />

      {/* Article specific meta tags */}
      {type === 'article' && article && (
        <>
          {article.publishedTime && (
            <meta property="article:published_time" content={article.publishedTime} />
          )}
          {article.modifiedTime && (
            <meta property="article:modified_time" content={article.modifiedTime} />
          )}
          {article.author && (
            <meta property="article:author" content={article.author} />
          )}
          {article.category && (
            <meta property="article:section" content={article.category} />
          )}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />

      {/* Structured Data */}
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;

// Pre-built SEO configurations for specific page types
export const createArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  publishedTime: string;
  modifiedTime?: string;
  author: string;
  category: string;
  url: string;
  language?: 'de' | 'en';
  wordCount?: number;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: article.title,
  description: article.description,
  ...(article.image ? { image: [article.image] } : {}),
  datePublished: article.publishedTime,
  dateModified: article.modifiedTime || article.publishedTime,
  author: {
    '@type': 'Person',
    name: article.author,
    url: `${BASE_URL}/ansatz`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Klanert',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/favicon.svg`,
    },
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': article.url,
  },
  articleSection: article.category,
  inLanguage: article.language === 'en' ? 'en' : 'de',
  ...(article.wordCount && article.wordCount > 0 ? { wordCount: article.wordCount } : {}),
  isAccessibleForFree: true,
});

export const createFAQSchema = (faqs: { question: string; answer: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const createBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});
